import { TippyProps } from '@tippyjs/react'

import { UserDetailsFragment } from '~generated/operations'
import { TaskStatusTypes, TaskTypes } from '~generated/types'

export const DEFAULT_PROFILE_PIC = '/onboarding/astronaut.png'

export enum Environments {
  DEVELOPMENT = 'development',
  STAGE = 'stage',
  PRODUCTION = 'production',
  LOCAL = 'local',
}

export enum PortalRoots {
  MODAL_ROOT = 'MODAL_ROOT',
  POPUP_ROOT = 'POPUP_ROOT',
  TOOLTIP_ROOT = 'TOOLTIP_ROOT',
  STEPS_ROOT = 'STEPS_ROOT',
}

export enum LayoutSections {
  LAYOUT = 'layout',
  CONTENT = 'layoutContent',
  HEADER = 'layoutHeader',
  BODY = 'layoutBody',
  LEFT_SIDEBAR = 'layoutLeftSidebar',
  RIGHT_SIDEBAR = 'layoutRightSidebar',
}

export enum LAUNCH_IMPACT {
  MAJOR = 'Major (Tier 1)',
  MEDIUM = 'Medium (Tier 2)',
  MINOR = 'Minor (Tier 3)',
}

export const PASSWORD_TOKEN = 'password_token'
export const INVITE_TOKEN = 'invite_token'
export const VERIFY_TOKEN = 'verify_token'
export const ENTRY = 'entry'
export const WEBSITE_EMAIL = 'email'
export const APPSUMO = 'appsumo'
export const STEP = 'step'

export enum AUTH_DATA_KEYS {
  ACCESS_TOKEN = 'accessToken',
  USER_ID = 'userId',
  ORGANIZATION_ID = 'organizationId',
  ORGANIZATION_NAME = 'organizationName',
  ORGANIZATION_SLUG = 'organizationSlug',
  IS_NEW_USER = 'isNewUser',
  IS_NEW_ORGANIZATION = 'isNewOrganization',
  LAST_ONBOARD_STEP = 'lastOnboardStep',
  HAS_CREATED_FIRST_LAUNCH = 'hasCreatedFirstLaunch',
  ORGANIZATION_FIRST_USER = 'firstUser',
}

export enum PUBLIC_LAUNCH_PAGE_COOKIE {
  ORGANIZATION_SLUG = 'organizationSlug',
  LAUNCH_ID = 'launchId',
}

export enum ORGANIZATION_TYPE {
  DEFAULT = 'Default',
  INVITE = 'Invite',
  DOMAIN = 'Domain',
  NEW = 'New',
}

export enum SUBSCRIPTION_DATA {
  IS_PAID_USER = 'isPaidUser',
  IS_TRIAL_ACTIVE = 'isTrialActive',
  PLAN_NAME = 'planName',
}

export enum CryptoLaunchTypes {
  NFT = 'NFT',
}

export const SAMPLE_LAUNCH_TYPE = 'Sample'
// TODO Remove all ERROR_TYPES after schema update (import them from backend)

export const AUTH_ERROR_TYPES = {
  USER_NOT_FOUND: 'User not found, please try signing up',
  EMAIL_IS_PUBLIC: 'Please sign up with your work email',
  USER_ALREADY_EXISTS: 'User already exists, try logging in',
  ERROR_CREATING_EMAIL_VERIFICATION_TOKEN: "Couldn't create email verification token, please try again",
  WRONG_CURRENT_PASSWORD: 'Please enter correct current password',
  UPDATE_PASSWORD_ERROR: 'Some error occured while updating your password',
  INVALID_EMAIL_CODE: 'Email code is invalid',
  EMAIL_SENT_RECENTLY: "We've recently sent an email, please try again after sometime",
  GENERIC_ERROR: 'Something went wrong, please try again',
  PASSWORD_AND_EMAIL_VERIFICATION_UPDATE_ERROR: "Can't directly update password or email verifiction status",
  ACCESS_DENIED: "You don't have permissions to perform this operation",
} as const

export const LAUNCH_ERROR_TYPES = {
  NOT_FOUND: 'Launch not found',
  TEMPLATE_NOT_FOUND: 'Template not found',
  MODULE_NOT_FOUND: 'Launch module not found',
  CREATE_TEMPLATE_MODULE_NOT_FOUND: 'Template module not found',
  ERROR_CREATING_LAUNCH_MODULE: 'Some error occurred while creating launch module',
  UPDATE_NOT_ALLOWED: 'Update is not allowed to users with this role',
  FORBIDDEN: 'This action is forbidden',
  NO_ACCESS: "You don't have required permissions to perform this operation",
  INVALID_UID: 'Invalid user ID found',
  INVALID_USER_ROLE: 'Invalid user role found',
  CANT_CREATE_TASK: 'Unable to create task for the launch',
  CANT_UPDATE_TASK: 'Unable to update task for the launch',
  OWNER_ADDITION_NOT_ALLOWED: "Owner can't be added",
  TASK_MANAGEMENT_NOT_EDITABLE: 'Task management editor is not editable',
  EDITOR_MIS_MATCH: 'Changes are not being made on the same editor type',
} as const

export const ORG_ERROR_TYPES = {
  URL_ALREADY_EXISTS: 'Organization already exists with the mentioned url',
  USER_NOT_FOUND: "Couldn't find any user with mentioned Organiation",
  USER_ADMIN_ROLE_CANT_BE_REMOVED: "Can't remove user with admin role",
  CANT_REMOVE_ONLY_ADMIN_FROM_ORGANIZATION: 'Removing only admin from organization is not allowed',
  COMPANY_ALREADY_EXISTS: 'Company already exists with the mentioned domain',
  ACCESS_DENIED: "You don't have permissions to do perform this operation",
  CDP_ACCOUNT_DOMAIN_ALREADY_EXISTS: 'Account already exists with the provided domain',
} as const

export enum PLAN_LAUNCH_STEPS {
  PLAN_LAUNCH = 'PLAN_LAUNCH',
  SELECT_TEMPLATE = 'SELECT_TEMPLATE',
}

export enum SHARE_PLANS_TYPES {
  SEND_INVITES = 'SEND_INVITES',
  SEND_UPDATES = 'SEND_UPDATES',
  SEND_REVIEW = 'SEND_REVIEW',
}

export const EDITOR_UPDATE_TIMEOUT = 800

export const HMDI_LOCALHOST_FIELD = 'HMDI'
export const FTUE_LOCALHOST_FIELD = 'FTUE'
export const COPILOT_LOCALSTORAGE_KEY = 'COPILOT'

export enum API {
  AWS_DOWNLOAD_FILE = '/api/aws/downloadFile',
  AWS_UPLOAD_FILE = '/api/aws/uploadFile',
}

export const SHARE_PLANS_EVENT = 'share-plans'

export const popupSettings = {
  trigger: 'click focus',
  placement: 'bottom-start' as TippyProps['placement'],
  offset: [0, 16] as TippyProps['offset'],
  flip: true,
}

export const SSO_TOKEN = 'sso_token'
export const SSO_ID = 'id'
export const SSO_EMAIL = 'sso_email'
export const SSO_FIRST_NAME = 'first_name'
export const SSO_LAST_NAME = 'last_name'

export const SSO_ERROR_TYPES = {
  LOGIN_ERROR: 'Somthing went wrong while logging in',
  GENERIC_ERROR: 'Something went wrong, please try again',
} as const

export const KEY_CODES = {
  arrowLeft: 'ArrowLeft',
  arrowRight: 'ArrowRight',
  arrowUp: 'ArrowUp',
  arrowDown: 'ArrowDown',
  enter: 'Enter',
  backspace: 'Backspace',
}

export const MAIL_TO = 'mailto:support@haveignition.com'

export enum CLICK_EVENTS {
  CLICKED_DASHBOARD = 'CLICKED DASHBOARD',
  CLICKED_TIMELINE = 'CLICKED TIMELINE',
  CLICKED_CALENDAR = 'CLICKED CALENDAR',
  CLICKED_LAUNCH_PLANS = 'CLICKED LAUNCH PLANS',
  CLICKED_LAUNCH_PLAN = 'CLICKED LAUNCH PLAN',
  CLICKED_RELEASE_NOTES = 'CLICKED RELEASE NOTES',
  CLICKED_PRODUCT_ROADMAP = 'CLICKED PRODUCT ROADMAP',
  CLICKED_PRODUCT_IDEAS = 'CLICKED PRODUCT IDEAS',
  CLICKED_BATTLECARDS = 'CLICKED BATTLECARDS',
  CLICKED_WIN_LOSS = 'CLICKED WIN/LOSS',
  CLICKED_NEWS = 'CLICKED NEWS',
  CLICKED_PERSONAS = 'CLICKED PERSONAS',
  CLICKED_ACCOUNTS = 'CLICKED ACCOUNTS',
  CLICKED_DEALS = 'CLICKED DEALS',
  CLICKED_STUDIES = 'CLICKED STUDIES',
  CLICKED_INSIGHTS = 'CLICKED INSIGHTS',
  CLICKED_TESTIMONIALS = 'CLICKED TESTIMONIALS',
  CLICKED_COPILOT = 'CLICKED COPILOT',
  CLICKED_SHARE_UPDATE = 'CLICKED SHARE UPDATE',
  CLICKED_SUBSCRIBERS = 'CLICKED SUBSCRIBERS',
  CLICKED_GENERATE = 'CLICKED GENERATE',
  CLICKED_INVITE_USER = 'CLICKED INVITE USER', // diz
  CLICKED_ONBOARDING = 'CLICKED_ONBOARDING',
  ADD_DEALS = 'Added deals',
  INTEGRATE_CRM = 'Integrated CRM',
  GENERATE_COPY = 'Generated copy',
}

export enum BILLING_EVENTS {
  CLICKED_UPGRADE = 'Clicked upgrade',
  NAVIGATED_BILLING_PAGE = 'Navigated to billing page', // diz
  SETUP_PAYMENT_METHOD = 'Clicked on setup a payment method',
  SHOWN_BILLING_GATE = 'Was shown a billing gate', // diz
}

export enum CREATE_EVENTS {
  CREATE_LAUNCH = 'CREATE LAUNCH',
  CREATE_ROADMAP = 'CREATE ROADMAP',
  CREATE_ROADMAP_ITEM = 'CREATE ROADMAP ITEM',
  CREATE_RELEASE = 'CREATE RELEASE',
  CREATE_RELEASE_NOTE = 'CREATE RELEASE NOTE',
  CREATE_PRODUCT_IDEA_BOARD = 'CREATE PRODUCT IDEA BOARD',
  CREATE_PRODUCT_IDEA = 'CREATE PRODUCT IDEA',
  CREATE_BATTLECARD = 'CREATE BATTLECARD',
  CREATE_TASK = 'CREATE TASK',
  CREATE_COPY_ASSET = 'CREATE COPY & ASSET MODULE',
  CREATE_SURVEY = 'CREATE SURVEY',
  CREATE_PERSONA = 'CREATE PERSONA',
  CREATE_PRODUCT_OKRS_OBJECTIVE = 'CREATE PRODUCT OKRS OBJECTIVE',
  CREATE_PRODUCT_OKRS_KPI = 'CREATE PRODUCT OKRS KPI',
  ADDED_INSIGHT = 'ADDED INSIGHT',
}

export enum ANALYTICS_EVENTS {
  LOGIN = 'LOGIN',
  LOGIN_PASSWORD = 'PASSWORD LOGIN',
  LOGIN_SSO = 'SSO LOGIN',
  LOGIN_GOOGLE = 'GOOGLE LOGIN',
  SIGNUP = 'SIGNUP',
  CREATED_LAUNCH = 'CREATED LAUNCH',
  SENT_UPDATE = 'SENT UPDATE',
  COPILOT = 'COPILOT',
  SUBSCRIPTION_UPDATED = 'SUBSCRIPTION UPDATED',
  CREATE_SURVEY = 'CREATE SURVEY',
  UPDATED_RELEASE_NOTES_PAGE = 'UPDATED RELEASE NOTES PAGE',
  CLICK = 'CLICK',
  NAVIGATE_FROM_WEBSITE = 'Navigated from website w/ UTM params',
}

export enum ANALYTICS_CONTENT_VIEWED {
  DASHBOARD = 'DASHBOARD',
  LAUNCH = 'LAUNCH',
  PLANS = 'PLANS',
  CALENDAR = 'CALENDAR',
  MODULE = 'MODULE',
  TASKS = 'TASKS',
  GTM_TIMELINE = 'GTM_TIMELINE',
}

export const AVATAR_COLORS = ['#EC7373', '#CD9854', '#486D6C', '#BA84FF', '#8489FF']

export const LABEL_COLORS = ['#FFAA79', '#EC7373', '#CD9854', '#486D6C', '#BA84FF', '#8489FF']

export enum DOCUMENT_EXPORT_OPTIONS {
  CONFLUENCE = 'CONFLUENCE',
  GOOGLE = 'GOOGLE',
  GOOGLE_SLIDES = 'GOOGLE_SLIDES',
}

export enum DOCUMENT_EXPORT_OPTIONS_LABELS {
  CONFLUENCE = 'Confluence',
  GOOGLE = 'Google Docs',
  GOOGLE_SLIDES = 'Google Slides',
}

export enum CONFLUENCE_COOKIES {
  CONFLUENCE_ACCESS_TOKEN = 'CONFLUENCE_ACCESS_TOKEN',
  CONFLUENCE_REFRESH_TOKEN = 'CONFLUENCE_REFRESH_TOKEN',
  CONFLUENCE_SCOPE = 'CONFLUENCE_SCOPE',
}

export enum GOOGLE_INTEGRATION_COOKIES {
  GOOGLE_ACCESS_TOKEN = 'GOOGLE_ACCESS_TOKEN',
}

export enum FEEDBACK_TYPE {
  INTERNAL = 'INTERNAL',
  PUBLIC = 'PUBLIC',
}

export enum WHITE_LABEL_THEMES {
  IGNITION = 'ignition',
  CRYPTO_CHICKS = 'crypto-chicks',
}

export const TASK_STATUS_LABELS = {
  [TaskStatusTypes.NOT_STARTED]: 'Todo',
  [TaskStatusTypes.IN_PROCESS]: 'In progress',
  [TaskStatusTypes.IN_REVIEW]: 'In review',
  [TaskStatusTypes.DONE]: 'Done',
  [TaskStatusTypes.CANCELED]: 'Canceled',
  [TaskStatusTypes.DELAYED]: 'Delayed',
}

export enum GOOGLE_SLIDE_API_ACTIONS {
  UPDATE_SLIDE_PAGE = 'update-slide-page',
  DELETE_SLIDE = 'delete-slide',
  ADD_SLIDE_PAGES = 'add-slide-pages',
  GET_ALL_FILES = 'get-all-files',
  GET_SLIDE = 'get-slide',
  CREATE_SLIDE = 'create-slide',
  COPY_SLIDE_TEMPLATE = 'copy-slide-template',
}

export enum BATTLECARDS_MEDIA_LINKS {
  TWITTER_BASE_LINK = 'https://twitter.com/',
  TWITTER_PREFIX = '@',
  LINKEDIN_BASE_LINK = 'https://www.linkedin.com/',
  LINKEDIN_PREFIX = 'lnkd.com/',
  COMPANY_BASE_LINK = 'https://',
  COMPANY_PREFIX = 'www.',
  SCREENSHOTS_BASE_LINK = 'https://battlecards-company-screenshots.s3.amazonaws.com/',
}

export const ORGANIZATION_SLUG_REG_EXP = /^(?!-)(?!.*-$)(?!.*--)[\da-z-]{1,63}$/i

export const LAUNCH_TASK_TYPES = [
  {
    label: 'Pre-launch',
    type: TaskTypes.PRE_LAUNCH,
  },
  {
    label: 'Launch',
    type: TaskTypes.LAUNCH,
  },
  {
    label: 'Post launch',
    type: TaskTypes.POST_LAUNCH,
  },
]

export const UNWANTED = {
  DOMAINS: ['pm.me', 'hey.com'],
  DOMAINS_ENDINGS: ['edu', 'gov'],
}

export const TESTING = {
  EMAILS: ['volhatest399@gmail.com'],
  DOMAINS_ENDINGS: ['ignitiontesting.com', 'igntesting.com'],
}

export const SMARTRENT_ORGANIZATION_ID = '62843b5928fcf30014bebc6b'

export enum ON_BOARDING_ORIGINS {
  RELEASE_NOTES = 'release-notes',
}

export enum BILLING_PLAN_NAMES {
  FREE_CHANGELOG = 'free changeLog',
  FREE_TRIAL = 'free trial',
  LAUNCHPAD = 'launchpad',
  LIFTOFF = 'liftoff',
  MOONSHOT = 'moonshot',
  APPSUMO_TIER1 = 'appsumo tier 1',
  APPSUMO_TIER2 = 'appsumo tier 2',
  APPSUMO_TIER3 = 'appsumo tier 3',
}

export const WEBSITE_LINK = 'https://haveignition.com'

/**
 * Unix timestamp (ms) for Feb 1, 2023 00:00:00 (GMT)
 */
export const FTUE_START_DATE = 1675209600000

export const GET_STARTED = 'get-started'

export type BaseUserDetailsFragment = Omit<UserDetailsFragment, 'primaryOnboardingGoal' | 'theme'>

/** Calendly link in booking call for integration support */
export const BOOK_CALL_LINK_INTEGRATION_SUPPORT = 'https://calendly.com/d/cpx8-gsd-kyx/ignition-support'

/** External Link in booking demo call */
export const BOOK_DEMO_CALL_LINK = 'https://www.haveignition.com/book-demo'
